import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  ExperienceTemplateStatus,
  IExperienceTemplate
} from '@dadohr/shared/domainTypes/experience';
import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography
} from '@material-ui/core';
import { Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ButtonWithPromise } from '../../../../../components/ButtonWithPromise';
import { DialogTitleWithTypography } from '../../../../../components/DialogTitleWithTypography';
import { useDialogState } from '../../../../../hooks/useDialogState';
import { useSnackbar } from '../../../../../hooks/useSnackbar';
import { useRoutes } from '../../../../../routes';
import { captureException } from '../../../../../sentry';
import { useCurrentUser } from '../../../../../services/currentUser';
import { deleteArchivedTemplate } from '../../../../../services/experiences/template';
import { updateDoc } from '../../../../../services/firebase';
import { now } from '../../../../../services/time';

export const FooterContainer = styled('div')`
  position: fixed;
  z-index: 2;
  background-color: rgba(255, 255, 255, 0.9);
  left: 0;
  bottom: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100vw;
  padding: ${(p) => p.theme.spacing(3)}px;
  padding-right: ${(p) => p.theme.spacing(5)}px;
`;

export const FooterContainerLeft = styled('div')`
  display: flex;
  justify-content: flex-start;
  align-items: center;

  > :not(:last-child) {
    margin-right: ${(p) => p.theme.spacing(2)}px;
  }
`;

export const FooterContainerRight = styled('div')`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${(p) => p.theme.spacing(2)}px;
`;

export const DiscardDraftOrCancelButton = React.memo(
  ({ d }: { d: Doc<IExperienceTemplate> }) => {
    const { ROUTES, goTo } = useRoutes();
    const { enqueueSnackbarSuccess, enqueueSnackbarError } = useSnackbar();
    const { t } = useTranslation();
    const { dialogOpen, openDialog, closeDialog } = useDialogState(false);
    if (d.data.status !== ExperienceTemplateStatus.ARCHIVED) {
      return null;
    }
    const title = t('Delete Experience');
    return (
      <>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<Trash2 size={16} />}
          onClick={openDialog}
        >
          {title}
        </Button>
        <Dialog open={dialogOpen} onClose={closeDialog}>
          <DialogTitleWithTypography>{title}?</DialogTitleWithTypography>
          <DialogContent>
            <Trans i18nKey={'Discard Draft Dialog Body'}>
              All data will be <strong>permanently</strong> deleted and cannot
              be recovered
            </Trans>
            {/* {t('Discard Draft Dialog Body')} */}
          </DialogContent>
          <DialogActions>
            <Button color="primary" onClick={closeDialog}>
              Cancel
            </Button>
            <ButtonWithPromise
              startIcon={<Trash2 size={16} />}
              pending={`${t('Discarding')}...`}
              color="secondary"
              variant="contained"
              onClick={() =>
                deleteArchivedTemplate(d).then(
                  () => {
                    enqueueSnackbarSuccess(t('Template Delete Success'));
                    goTo(ROUTES.experiences.overview.url());
                  },
                  () => {
                    enqueueSnackbarError(t('Template Delete Error'));
                  }
                )
              }
            >
              {title}
            </ButtonWithPromise>
          </DialogActions>
        </Dialog>
      </>
    );
  },
  (prev, next) => {
    return (
      prev.d.id === next.d.id &&
      prev.d.collection === next.d.collection &&
      prev.d.data.status === next.d.data.status
    );
  }
);

const AutoSaveIndicatorContainer = styled('div')<{ saved?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing(0.75)}px;
  background-color: ${(p) => (p.saved ? 'rgb(232, 245, 233)' : 'rgb(254, 250, 236)')};
  padding: ${(p) => p.theme.spacing(0.75)}px ${(p) => p.theme.spacing(2)}px;
  border-radius: 20px;
  transition: background-color 0.3s ease;
`;

const PulsingDot = styled('span')<{ active?: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(p) => (p.active ? '#4caf50' : '#ff9800')};
  display: inline-block;
  animation: ${(p) => (p.active ? 'none' : 'pulse 2s ease-in-out infinite')};

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
`;

const getSecondsAgo = (date: Date) =>
  Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));

const formatTimeAgo = (seconds: number) => {
  if (seconds < 5) return 'Last saved: just now';
  if (seconds < 60) return `Last saved: ${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  return `Last saved: ${minutes}m ago`;
};

const AutoSaveIndicator = ({ lastSavedAt, isRoughDraft }: { lastSavedAt: Date | null; isRoughDraft: boolean }) => {
  const [secondsAgo, setSecondsAgo] = useState(() => lastSavedAt ? getSecondsAgo(lastSavedAt) : -1);

  useEffect(() => {
    if (!lastSavedAt) return;
    setSecondsAgo(getSecondsAgo(lastSavedAt));
    const interval = setInterval(
      () => setSecondsAgo(getSecondsAgo(lastSavedAt)),
      5000
    );
    return () => clearInterval(interval);
  }, [lastSavedAt]);

  if (!isRoughDraft) return null;

  const hasSaved = lastSavedAt !== null;
  const label = hasSaved
    ? formatTimeAgo(secondsAgo)
    : 'Auto-save enabled';

  return (
    <AutoSaveIndicatorContainer saved={hasSaved && secondsAgo < 5}>
      <PulsingDot active={hasSaved} />
      <Typography variant="body2" style={{ fontSize: 13, fontWeight: 500, color: hasSaved ? '#2e7d32' : '#f57c00' }}>
        {label}
      </Typography>
    </AutoSaveIndicatorContainer>
  );
};

export const StandardFooter = ({
  d,
  onCheckChanges,
  onLaunchFromRoughDraft,
  isLaunchReady,
  onLaunch,
  lastSavedAt
}: {
  d: Doc<IExperienceTemplate>;
  // ROUGH_DRAFT: two-button split. "Check Changes" validates + shows the
  // checklist; "Launch" stays disabled until validation passed with no
  // edits since (isLaunchReady).
  onCheckChanges?: () => Promise<void>;
  onLaunchFromRoughDraft?: () => Promise<void>;
  isLaunchReady?: boolean;
  // DRAFT path (unchanged): go to Review.
  onLaunch?: () => void;
  lastSavedAt?: Date | null;
}) => {
  const { id: updatedBy } = useCurrentUser();
  const { t } = useTranslation();

  const update = async () => {
    try {
      await updateDoc(d, (x) => {
        const next = {
          ...x,
          updatedBy,
          updatedAt: now()
        };
        return next;
      });
    } catch (err) {
      console.error('Failed to save experience template:', err);
      captureException(err, {
        tags: {
          feature: 'experience_template_save',
          templateId: d.id
        },
        extra: {
          templateStatus: d.data.status,
          updatedBy
        }
      });
      throw err;
    }
  };

  const saving = `${t('Saving')}...`;

  return (
    <FooterContainer>
      <FooterContainerLeft>
        <DiscardDraftOrCancelButton d={d} />
      </FooterContainerLeft>
      <FooterContainerRight>
        <AutoSaveIndicator
          lastSavedAt={lastSavedAt ?? null}
          isRoughDraft={d.data.status === ExperienceTemplateStatus.ROUGH_DRAFT}
        />
        <ButtonWithPromise
          color="primary"
          pending={saving}
          onClick={() => update()}
        >
          {t('Save changes')}
        </ButtonWithPromise>

        {onCheckChanges && (
          <ButtonWithPromise
            color="primary"
            pending={`${t('Checking')}...`}
            onClick={onCheckChanges}
          >
            {t('Check Changes')}
          </ButtonWithPromise>
        )}

        {onLaunchFromRoughDraft && (
          // Disabled until Check Changes passed with no edits since.
          // onLaunchFromRoughDraft re-validates as a last-line safety net.
          <span style={!isLaunchReady ? { cursor: 'not-allowed' } : undefined}>
            <ButtonWithPromise
              variant="contained"
              color="primary"
              disabled={!isLaunchReady}
              pending={`${t('Launching')}...`}
              onClick={onLaunchFromRoughDraft}
            >
              {t('Launch')}
            </ButtonWithPromise>
          </span>
        )}

        {onLaunch && (
          <Button
            variant="contained"
            color="primary"
            onClick={onLaunch}
          >
            {t('Launch')}
          </Button>
        )}
      </FooterContainerRight>
    </FooterContainer>
  );
};
